import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "../../lib/api";
import { serviceName, STAGES } from "../../lib/constants";
import { StatCard } from "../../components/shared/StatCard";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { DollarSign, Rocket, FileText, Users, Truck, Receipt, Activity, Loader2, CalendarCheck2, CheckCircle2, Play } from "lucide-react";

const CHART_COLORS = ["#0EA5E9", "#3B82F6", "#14B8A6", "#64748B", "#38BDF8", "#22D3EE", "#94A3B8"];

const Tile = ({ label, value, sub, testId, icon: Icon, to }) => {
  const inner = (
    <>
      <p className="stat-label flex items-center gap-1.5 truncate">{Icon && <Icon className="w-3.5 h-3.5 shrink-0 group-hover:text-[var(--cc-accent)] transition-colors duration-200" />}<span className="truncate">{label}</span></p>
      <p className="mt-2 font-heading font-extrabold text-xl sm:text-2xl tracking-tight">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</p>}
    </>
  );
  const cls = "group bg-card p-4 min-w-0 transition-all duration-200 hover:bg-[var(--cc-accent)]/[0.04] hover:shadow-[inset_0_0_0_1px_var(--cc-accent)]";
  return to ? <Link to={to} data-testid={testId} className={cls}>{inner}</Link> : <div data-testid={testId} className={cls}>{inner}</div>;
};

const PanelHead = ({ eyebrow, title, right }) => (
  <div className="flex items-center justify-between gap-3 min-w-0">
    <div className="min-w-0">
      {eyebrow && <p className="stat-label">{eyebrow}</p>}
      <h2 className="font-heading font-bold tracking-tight truncate">{title}</h2>
    </div>
    {right}
  </div>
);

export default function OpsDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/ops/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data)
    return (
      <div className="flex items-center justify-center gap-3 py-24 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Initializing command deck…
      </div>
    );
  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="ops-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline-cc">Operations Command</p>
          <h1 className="page-title mt-1.5">Executive Dashboard</h1>
        </div>
        <span className="font-mono-cc text-[11px] tracking-[0.14em] uppercase text-muted-foreground" data-testid="dashboard-date">
          {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
        </span>
      </div>

      {/* Hero KPI row — revenue dominates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/ops/finance" data-testid="kpi-revenue"
          className="group sm:col-span-2 corner-cc rounded-xl border border-[var(--cc-accent)]/45 bg-card p-5 sm:p-6 min-w-0 transition-all duration-200 hover:shadow-[0_0_0_1px_var(--cc-accent),0_16px_40px_-16px_var(--cc-accent)]">
          <div className="flex items-start justify-between gap-2">
            <p className="stat-label">Revenue — Collected</p>
            <span className="w-8 h-8 rounded-md border border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/10 flex items-center justify-center shrink-0">
              <DollarSign className="w-4 h-4 text-[var(--cc-accent)]" />
            </span>
          </div>
          <p className="mt-3 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl break-words">{fmtMoney(k.revenue)}</p>
          <p className="mt-2 text-xs text-muted-foreground">Paid invoices · all time · <span className="text-[var(--cc-accent)] group-hover:underline">Open finance →</span></p>
        </Link>
        <StatCard testId="kpi-todays-ops" label="Today's Operations" value={k.todays_operations} icon={Rocket} to="/ops/missions" />
        <StatCard testId="kpi-pending-quotes" label="Pending Quotes" value={k.pending_quotes} icon={FileText} to="/ops/quotes" />
      </div>

      {/* Ops status strip */}
      <div className="rounded-xl border overflow-hidden bg-border grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px" data-testid="ops-status-strip">
        <Tile testId="kpi-booked" label="Booked" value={k.booked_jobs} icon={CalendarCheck2} to="/ops/missions?status=booked" />
        <Tile testId="kpi-in-progress" label="In Progress" value={k.in_progress} icon={Play} to="/ops/missions?status=in_progress" />
        <Tile testId="kpi-completed" label="Completed" value={k.completed} icon={CheckCircle2} to="/ops/missions?status=completed" />
        <Tile testId="kpi-open-invoices" label="Open Invoices" value={k.open_invoices} sub={fmtMoney(k.open_invoices_value) + " out"} icon={Receipt} to="/ops/invoices" />
        <Tile testId="kpi-crew" label="Crew Avail" value={`${k.crew_available}/${k.crew_total}`} icon={Users} to="/ops/resources" />
        <Tile testId="kpi-vehicles" label="Vehicles" value={`${k.vehicles_ready}/${k.vehicles_total}`} icon={Truck} to="/ops/resources" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border bg-card p-6">
          <div className="mb-6">
            <PanelHead eyebrow="Revenue" title="Last 6 Months"
              right={<span className="flex items-center gap-2 font-mono-cc text-[10px] tracking-[0.2em] uppercase text-emerald-500"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" /> Live</span>} />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.revenue_chart}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--cc-accent)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="var(--cc-accent)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="revenue" stroke="var(--cc-accent)" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border bg-card p-6">
          <div className="mb-4"><PanelHead eyebrow="Distribution" title="Service Mix" /></div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={data.service_breakdown} dataKey="value" nameKey="name" innerRadius={50} outerRadius={75} paddingAngle={3}>
                {data.service_breakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2 max-h-24 overflow-y-auto">
            {data.service_breakdown.map((s, i) => (
              <p key={s.name} className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} /> {s.name} — {s.value}
              </p>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="rounded-xl border bg-card">
          <div className="px-6 py-4 border-b">
            <PanelHead eyebrow="Today" title="Today's Missions"
              right={<Link to="/ops/dispatch" className="text-xs text-[var(--cc-accent)] font-semibold shrink-0">Dispatch →</Link>} />
          </div>
          <div className="divide-y max-h-80 overflow-y-auto">
            {data.todays_missions.length === 0 && <p className="p-5 text-sm text-muted-foreground">No missions scheduled today.</p>}
            {data.todays_missions.map((m) => (
              <div key={m.id} className="p-4" data-testid={`today-mission-${m.mission_number}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-mono-cc text-xs text-[var(--cc-accent)]">{m.mission_number} · {m.scheduled_time}</p>
                  <StatusBadge status={m.status} />
                </div>
                <p className="text-sm font-semibold mt-1.5">{m.customer_name} — {serviceName(m.service_type)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border bg-card">
          <div className="px-6 py-4 border-b">
            <PanelHead eyebrow="Signal" title="Activity Feed"
              right={<Activity className="w-4 h-4 text-[var(--cc-accent)] shrink-0" />} />
          </div>
          <div className="divide-y max-h-80 overflow-y-auto">
            {data.activities.map((a) => (
              <div key={a.id} className="p-4">
                <p className="text-sm leading-snug">{a.message}</p>
                <p className="text-[11px] text-muted-foreground font-mono-cc mt-1">{new Date(a.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border bg-card">
            <div className="px-6 py-4 border-b">
              <PanelHead eyebrow="Funnel" title="Pipeline"
                right={<Link to="/ops/crm" className="text-xs text-[var(--cc-accent)] font-semibold shrink-0">Open CRM →</Link>} />
            </div>
            <div className="p-5 space-y-2.5">
              {STAGES.slice(0, 6).map((s) => (
                <div key={s.id} className="flex items-center gap-3">
                  <span className="text-xs w-24 text-muted-foreground">{s.name}</span>
                  <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full bg-[var(--cc-accent)]" style={{ width: `${Math.min(100, (data.pipeline[s.id] || 0) * 20)}%` }} />
                  </div>
                  <span className="font-mono-cc text-xs w-5 text-right">{data.pipeline[s.id] || 0}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border bg-card">
            <div className="px-6 py-4 border-b"><PanelHead eyebrow="Accounts" title="Recent Customers" /></div>
            <div className="divide-y">
              {data.recent_customers.slice(0, 4).map((c) => (
                <div key={c.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--cc-accent)]/15 text-[var(--cc-accent)] flex items-center justify-center font-heading font-bold text-xs">{c.name[0]}</div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{c.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{c.email}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
